/**
 * Consultations.tsx — Módulo de Consultas
 *
 * Tabla con historial completo de sesiones del terapeuta (o de toda la clínica
 * en modo Director). Lee desde Supabase vía RPCs SECURITY DEFINER con IDOR.
 *
 * Contexto B — Portal Terapeuta (TherapistPortal.css tokens)
 *
 * Restricciones:
 * - Sin console.log con datos de pacientes
 * - Sin servicios externos para avatares
 * - Sin tokens del Contexto A (modern-style.css)
 */

import React, { useState, useEffect, useMemo } from 'react';
import './Consultations.css';
import { supabase } from '../../lib/supabase/client';
import { TransbankService } from '../../services/transbank.service';
import { useConfirm } from '../ui/ConfirmProvider';
import { useToast } from '../ui/ToastProvider';
import { TableSkeleton } from '../ui/Skeleton';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface ConsultationsModuleProps {
  therapist: { id: string; nombre: string; apellido: string };
  adminMode?: boolean;
  isDirector?: boolean;
}

interface SessionRow {
  id: string;
  fecha_hora: string;
  duracion: number;
  tipo_consulta: string;
  modalidad: string;
  estado: string;
  paciente_nombre: string;
  paciente_apellido: string;
  terapeuta_nombre?: string;
  terapeuta_apellido?: string;
  valor_final: number;
  estado_pago_consulta?: string;
  numero_comprobante: string | null;
  numero_boleta_sii: string | null;
}

/** Campos de registro manual de pago editables inline (terapeuta y director). */
type PagoManualField = 'numero_comprobante' | 'numero_boleta_sii';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCLP(n: number): string {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
  }).format(n);
}

function normalizeTipo(tipo: string): string {
  const map: Record<string, string> = {
    'Terapia Individual': 'Individual',
    'Terapia Individual - Sesión Extendida': 'Indiv. Extendida',
    'Terapia Individual - Sesion Extendida': 'Indiv. Extendida',
    'Terapia de Parejas': 'Parejas',
    'Terapia de Pareja': 'Parejas',
    'Terapia Familiar': 'Familiar',
    individual: 'Individual',
    pareja: 'Parejas',
    familiar: 'Familiar',
  };
  return map[tipo] ?? tipo;
}

// ─── Badge estado pago ────────────────────────────────────────────────────────

function PaymentStatusBadge({ estado }: { estado: string | undefined }) {
  if (!estado || estado === 'sin_pago') return null;

  const cfg: Record<string, { label: string; bg: string; color: string }> = {
    anticipo_pendiente: { label: 'Anticipo pendiente', bg: '#FFF3CD', color: '#856404' },
    anticipo_pagado:    { label: 'Anticipo ✓',         bg: '#D1FAE5', color: '#065F46' },
    pago_completo:      { label: 'Pagado ✓',           bg: '#BBFBD0', color: '#065F46' },
  };
  const c = cfg[estado];
  if (!c) return null;

  return (
    <span style={{
      display: 'inline-block',
      background: c.bg,
      color: c.color,
      fontSize: '0.72rem',
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: 20,
      whiteSpace: 'nowrap',
    }}>
      {c.label}
    </span>
  );
}

// ─── Badge estado ─────────────────────────────────────────────────────────────

function SessionStatusBadge({ estado }: { estado: string }) {
  const modifiers: Record<string, string> = {
    completada: 'cons-badge--green',
    cancelada:  'cons-badge--red',
    programada: 'cons-badge--blue',
    agendada:   'cons-badge--blue',
    confirmada: 'cons-badge--blue',
    no_asistio: 'cons-badge--orange',
    en_progreso: 'cons-badge--purple',
  };
  const labels: Record<string, string> = {
    completada:  'Completada',
    cancelada:   'Cancelada',
    programada:  'Programada',
    agendada:    'Agendada',
    confirmada:  'Confirmada',
    no_asistio:  'No asistió',
    en_progreso: 'En progreso',
  };
  return (
    <span className={`cons-badge ${modifiers[estado] ?? 'cons-badge--blue'}`}>
      {labels[estado] ?? estado}
    </span>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const ConsultationsModule: React.FC<ConsultationsModuleProps> = ({
  therapist,
  adminMode = false,
  isDirector = false,
}) => {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filtros
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterDesde, setFilterDesde] = useState('');
  const [filterHasta, setFilterHasta] = useState('');

  const confirm = useConfirm();
  const toast = useToast();

  // Eliminación de consulta
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteConsultation = async (id: string) => {
    const row = sessions.find(s => s.id === id);
    if (!row) return;
    const confirmed = await confirm({
      title: '¿Eliminar esta consulta permanentemente?',
      message: `Paciente: ${row.paciente_nombre} ${row.paciente_apellido}\nFecha: ${formatDate(row.fecha_hora)} · ${formatTime(row.fecha_hora)}\n\nEsta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const { error } = await supabase.rpc('delete_consultation', {
        p_consultation_id: id,
      });
      if (error) throw error;
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('[Consultations] Error al eliminar consulta:', err);
      toast.error('No se pudo eliminar la consulta. Intentá nuevamente.');
    } finally {
      setDeletingId(null);
    }
  };

  // Cancelación de consulta (soft-cancel + reembolso automático si aplica)
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const handleCancelConsultation = async (id: string) => {
    const row = sessions.find(s => s.id === id);
    if (!row) return;
    const confirmed = await confirm({
      title: '¿Cancelar esta consulta?',
      message: `Paciente: ${row.paciente_nombre} ${row.paciente_apellido}\nFecha: ${formatDate(row.fecha_hora)} · ${formatTime(row.fecha_hora)}\n\nSi el anticipo fue pagado con Webpay, se reembolsará automáticamente.`,
      confirmLabel: 'Cancelar consulta',
      cancelLabel: 'Volver',
      tone: 'danger',
    });
    if (!confirmed) return;

    setCancelingId(id);
    try {
      const { data: consultaData } = await supabase
        .from('consultas')
        .select('estado_pago_consulta, pago_anticipo_id')
        .eq('id', id)
        .single();

      if (consultaData?.estado_pago_consulta === 'anticipo_pagado' && consultaData?.pago_anticipo_id) {
        try {
          // SEC-076: no hace falta pasar nada extra acá — corre con la sesión
          // de Supabase Auth del portal (terapeuta o director), y
          // `functions.invoke` reenvía ese JWT en el header Authorization. El
          // Edge Function resuelve la identidad desde ahí y valida ownership
          // (dueño de la consulta, o director) antes de reembolsar.
          await TransbankService.refund(consultaData.pago_anticipo_id);
        } catch (refundErr) {
          console.error('[Consultations] Error en reembolso Transbank (no bloquea cancelación):', refundErr);
        }
      }

      const { error } = await supabase.rpc('cancel_consultation_terapeuta', {
        p_consultation_id: id,
      });
      if (error) throw error;

      setSessions(prev =>
        prev.map(s => s.id === id ? { ...s, estado: 'cancelada' } : s)
      );
    } catch (err) {
      console.error('[Consultations] Error al cancelar consulta:', err);
      toast.error('No se pudo cancelar la consulta. Intentá nuevamente.');
    } finally {
      setCancelingId(null);
    }
  };

  // Edición inline de precio (solo director)
  const [editingPrecioId, setEditingPrecioId] = useState<string | null>(null);
  const [editingPrecioValue, setEditingPrecioValue] = useState<string>('');
  const [precioSaveError, setPrecioSaveError] = useState<string | null>(null);

  // Edición inline de estado de pago (terapeuta y director)
  const [editingPagoId, setEditingPagoId]     = useState<string | null>(null);
  const [pagoSaving,    setPagoSaving]         = useState(false);

  // Edición inline de código de transferencia / número de boleta (terapeuta y director)
  // Un solo editor activo a la vez en toda la tabla (mismo criterio que editingPrecioId).
  const [editingManualField, setEditingManualField] = useState<{ id: string; field: PagoManualField } | null>(null);
  const [editingManualValue, setEditingManualValue] = useState<string>('');
  const [manualSaveError,    setManualSaveError]     = useState<string | null>(null);

  // ── Carga de datos ──────────────────────────────────────────────────────────

  useEffect(() => {
    setSessions([]);
    setOffset(0);
    fetchSessions(0, true);
  }, [therapist.id, adminMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSessions = async (newOffset: number, reset = false) => {
    setLoading(true);
    setError(null);
    try {
      let data: SessionRow[] | null = null;
      let rpcError: { message: string } | null = null;

      if (adminMode) {
        const result = await supabase.rpc('get_all_sessions_director', {
          p_limit:  PAGE_SIZE + 1,
          p_offset: newOffset,
        });
        data = result.data as SessionRow[] | null;
        rpcError = result.error;
      } else {
        const result = await supabase.rpc('get_sessions_for_therapist', {
          p_therapist_id: therapist.id,
          p_limit:        PAGE_SIZE + 1,
          p_offset:       newOffset,
        });
        data = result.data as SessionRow[] | null;
        rpcError = result.error;
      }

      if (rpcError) {
        console.error('[Consultations] Supabase RPC error:', rpcError);
        throw new Error(rpcError.message);
      }

      const rows = data ?? [];
      const hasNextPage = rows.length > PAGE_SIZE;
      const pageRows = hasNextPage ? rows.slice(0, PAGE_SIZE) : rows;

      setSessions(prev => reset ? pageRows : [...prev, ...pageRows]);
      setHasMore(hasNextPage);
      setOffset(newOffset + PAGE_SIZE);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[Consultations] RPC error:', msg);
      setError('No se pudo cargar el historial de consultas.');
    } finally {
      setLoading(false);
    }
  };

  // ── Filtrado local ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return sessions.filter(s => {
      if (filterEstado && s.estado !== filterEstado) return false;
      if (filterTipo && !(s.tipo_consulta ?? '').toLowerCase().includes(filterTipo.toLowerCase())) return false;
      if (filterDesde && new Date(s.fecha_hora) < new Date(filterDesde)) return false;
      if (filterHasta) {
        const hasta = new Date(filterHasta);
        hasta.setHours(23, 59, 59);
        if (new Date(s.fecha_hora) > hasta) return false;
      }
      return true;
    });
  }, [sessions, filterEstado, filterTipo, filterDesde, filterHasta]);

  // ── Stats del período filtrado ──────────────────────────────────────────────

  const stats = useMemo(() => {
    const total       = filtered.length;
    const completadas = filtered.filter(s => s.estado === 'completada').length;
    const canceladas  = filtered.filter(s => s.estado === 'cancelada').length;
    const ingresos    = filtered
      .filter(s => s.estado === 'completada')
      .reduce((sum, s) => sum + (s.valor_final ?? 0), 0);
    return { total, completadas, canceladas, ingresos };
  }, [filtered]);

  // ── Edición de precio (solo director) ──────────────────────────────────────

  const startEditingPrecio = (id: string, currentValue: number) => {
    setEditingPrecioId(id);
    setEditingPrecioValue(String(currentValue ?? 0));
  };

  const cancelEditingPrecio = () => {
    setEditingPrecioId(null);
    setEditingPrecioValue('');
    setPrecioSaveError(null);
  };

  const savePrecio = async (id: string) => {
    const newValue = Number(editingPrecioValue);
    if (isNaN(newValue) || newValue < 0) {
      cancelEditingPrecio();
      return;
    }
    setPrecioSaveError(null);
    const { error: updateError } = await supabase.rpc('update_consulta_precio_director', {
      p_consulta_id:  id,
      p_nuevo_precio: newValue,
    });
    if (updateError) {
      setPrecioSaveError('No se pudo guardar el precio. Intentá nuevamente.');
      return;
    }
    setSessions(prev =>
      prev.map(s => s.id === id ? { ...s, valor_final: newValue } : s)
    );
    cancelEditingPrecio();
  };

  // ── Actualizar estado de pago ───────────────────────────────────────────────

  const savePago = async (id: string, nuevoEstado: string) => {
    setPagoSaving(true);
    const { error: updateError } = await supabase
      .from('consultas')
      .update({ estado_pago_consulta: nuevoEstado })
      .eq('id', id);

    if (!updateError) {
      setSessions(prev =>
        prev.map(s => s.id === id ? { ...s, estado_pago_consulta: nuevoEstado } : s)
      );
    }
    setEditingPagoId(null);
    setPagoSaving(false);
  };

  // ── Edición de código de transferencia / número de boleta (terapeuta y director) ──

  const startEditingManual = (id: string, field: PagoManualField, currentValue: string | null) => {
    setEditingManualField({ id, field });
    setEditingManualValue(currentValue ?? '');
    setManualSaveError(null);
  };

  const cancelEditingManual = () => {
    setEditingManualField(null);
    setEditingManualValue('');
    setManualSaveError(null);
  };

  const saveManualField = async (id: string, field: PagoManualField) => {
    const trimmed = editingManualValue.trim();
    setManualSaveError(null);

    // Cada campo se guarda con su propia llamada a la RPC, mandando solo el
    // parámetro de ese campo — el otro se omite (no se toca en el backend).
    const { error: rpcError } = field === 'numero_comprobante'
      ? await supabase.rpc('set_pago_manual_data', {
          p_consulta_id: id,
          p_codigo_transferencia: trimmed,
        })
      : await supabase.rpc('set_pago_manual_data', {
          p_consulta_id: id,
          p_numero_boleta: trimmed,
        });

    if (rpcError) {
      console.error('[Consultations] Error al guardar dato manual de pago:', rpcError);
      setManualSaveError('No se pudo guardar. Intentá nuevamente.');
      return;
    }

    const newValue = trimmed === '' ? null : trimmed;
    setSessions(prev =>
      prev.map(s => s.id === id ? { ...s, [field]: newValue } : s)
    );
    cancelEditingManual();
  };

  const renderManualCell = (s: SessionRow, field: PagoManualField) => {
    const isEditing = editingManualField?.id === s.id && editingManualField.field === field;
    const value = s[field];

    if (isEditing) {
      return (
        <div className="cons-manual-edit">
          <input
            className="cons-manual-input"
            type="text"
            value={editingManualValue}
            onChange={e => { setEditingManualValue(e.target.value); setManualSaveError(null); }}
            onBlur={() => saveManualField(s.id, field)}
            onKeyDown={e => {
              if (e.key === 'Enter') saveManualField(s.id, field);
              if (e.key === 'Escape') cancelEditingManual();
            }}
            autoFocus
          />
          {manualSaveError && (
            <span className="cons-manual-error">{manualSaveError}</span>
          )}
        </div>
      );
    }

    return (
      <span
        className="cons-manual-editable"
        title="Clic para editar"
        onClick={() => startEditingManual(s.id, field, value)}
      >
        {value ?? <span className="cons-manual-empty">— agregar</span>}
      </span>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading && sessions.length === 0) {
    return (
      <div className="consultations">
        <TableSkeleton rows={9} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="cons-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button className="cons-btn cons-btn--secondary" onClick={() => fetchSessions(0, true)}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="consultations">
      {/* Encabezado */}
      <div className="cons-header">
        <div className="cons-header__title-group">
          <h1 className="cons-title">{adminMode ? 'Todas las Consultas' : 'Mis Consultas'}</h1>
          <p className="cons-subtitle">
            {adminMode
              ? 'Vista director — historial completo de la clínica'
              : 'Historial de sesiones y estado de cada consulta'}
          </p>
        </div>
      </div>

      {/* Stats chips */}
      <div className="cons-stats-row">
        <div className="cons-stat-chip">
          <span className="cons-stat-chip__value">{stats.total}</span>
          <span className="cons-stat-chip__label">Total</span>
        </div>
        <div className="cons-stat-chip cons-stat-chip--green">
          <span className="cons-stat-chip__value">{stats.completadas}</span>
          <span className="cons-stat-chip__label">Completadas</span>
        </div>
        <div className="cons-stat-chip cons-stat-chip--red">
          <span className="cons-stat-chip__value">{stats.canceladas}</span>
          <span className="cons-stat-chip__label">Canceladas</span>
        </div>
        <div className="cons-stat-chip cons-stat-chip--blue">
          <span className="cons-stat-chip__value">{formatCLP(stats.ingresos)}</span>
          <span className="cons-stat-chip__label">Ingresos</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="cons-filters">
        <select
          className="cons-select"
          value={filterEstado}
          onChange={e => setFilterEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="agendada">Agendada</option>
          <option value="confirmada">Confirmada</option>
          <option value="completada">Completada</option>
          <option value="cancelada">Cancelada</option>
          <option value="no_asistio">No asistió</option>
        </select>

        <select
          className="cons-select"
          value={filterTipo}
          onChange={e => setFilterTipo(e.target.value)}
        >
          <option value="">Todos los tipos</option>
          <option value="Individual">Individual</option>
          <option value="Pareja">Parejas</option>
          <option value="Familiar">Familiar</option>
        </select>

        <div className="cons-filter-dates">
          <input
            type="date"
            className="cons-input-date"
            value={filterDesde}
            onChange={e => setFilterDesde(e.target.value)}
            title="Desde"
          />
          <span className="cons-filter-dates__sep">—</span>
          <input
            type="date"
            className="cons-input-date"
            value={filterHasta}
            onChange={e => setFilterHasta(e.target.value)}
            title="Hasta"
          />
        </div>

        {(filterEstado || filterTipo || filterDesde || filterHasta) && (
          <button
            className="cons-btn cons-btn--ghost"
            onClick={() => {
              setFilterEstado('');
              setFilterTipo('');
              setFilterDesde('');
              setFilterHasta('');
            }}
          >
            <i className="fas fa-times"></i> Limpiar
          </button>
        )}
      </div>

      {/* Tabla */}
      {filtered.length === 0 ? (
        <div className="cons-empty">
          <i className="fas fa-clipboard-list"></i>
          <p>No hay consultas que coincidan con los filtros.</p>
        </div>
      ) : (
        <div className="cons-table-wrapper">
          <table className="cons-table">
            <thead>
              <tr>
                <th>Fecha</th>
                {adminMode && <th>Terapeuta</th>}
                <th>Paciente</th>
                <th>Tipo</th>
                <th>Modalidad</th>
                <th>Duración</th>
                <th>Estado</th>
                <th>Pago</th>
                <th>Cód. transferencia</th>
                <th>N° boleta</th>
                <th className="cons-th--right">Valor</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="cons-row">
                  <td className="cons-td--date">
                    <span className="cons-date">{formatDate(s.fecha_hora)}</span>
                    <span className="cons-time">{formatTime(s.fecha_hora)}</span>
                  </td>
                  {adminMode && (
                    <td>
                      <span className="cons-name">
                        {s.terapeuta_nombre} {s.terapeuta_apellido}
                      </span>
                    </td>
                  )}
                  <td>
                    <span className="cons-name">
                      {s.paciente_nombre} {s.paciente_apellido}
                    </span>
                  </td>
                  <td>
                    <span className="cons-tipo">{normalizeTipo(s.tipo_consulta)}</span>
                  </td>
                  <td>
                    <span className={`cons-modalidad cons-modalidad--${s.modalidad}`}>
                      {s.modalidad === 'online' ? (
                        <><i className="fas fa-video"></i> Online</>
                      ) : (
                        <><i className="fas fa-building"></i> Presencial</>
                      )}
                    </span>
                  </td>
                  <td className="cons-td--center">{s.duracion} min</td>
                  <td><SessionStatusBadge estado={s.estado} /></td>
                  <td>
                    {editingPagoId === s.id ? (
                      <select
                        className="cons-select"
                        style={{ fontSize: '0.8rem', padding: '2px 6px' }}
                        defaultValue={s.estado_pago_consulta ?? 'sin_pago'}
                        disabled={pagoSaving}
                        autoFocus
                        onChange={e => savePago(s.id, e.target.value)}
                        onBlur={() => setEditingPagoId(null)}
                      >
                        <option value="sin_pago">Sin pago</option>
                        <option value="anticipo_pendiente">Anticipo pendiente</option>
                        <option value="anticipo_pagado">Anticipo pagado</option>
                        <option value="pago_completo">Pago completo</option>
                      </select>
                    ) : (
                      <span
                        style={{ cursor: 'pointer' }}
                        title="Clic para editar estado de pago"
                        onClick={() => setEditingPagoId(s.id)}
                      >
                        <PaymentStatusBadge estado={s.estado_pago_consulta} />
                        {(!s.estado_pago_consulta || s.estado_pago_consulta === 'sin_pago') && (
                          <span style={{ color: '#CBD5E0', fontSize: '0.75rem' }}>— editar</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="cons-td--manual">
                    {renderManualCell(s, 'numero_comprobante')}
                  </td>
                  <td className="cons-td--manual">
                    {renderManualCell(s, 'numero_boleta_sii')}
                  </td>
                  <td className="cons-td--right cons-valor">
                    {isDirector && editingPrecioId === s.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        <input
                          className="cons-precio-input"
                          type="number"
                          min={0}
                          value={editingPrecioValue}
                          onChange={e => { setEditingPrecioValue(e.target.value); setPrecioSaveError(null); }}
                          onBlur={() => savePrecio(s.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') savePrecio(s.id);
                            if (e.key === 'Escape') cancelEditingPrecio();
                          }}
                          autoFocus
                        />
                        {precioSaveError && editingPrecioId === s.id && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--error-text, #C4281B)', whiteSpace: 'nowrap' }}>
                            {precioSaveError}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span
                        className={isDirector ? 'cons-valor-editable' : undefined}
                        onClick={isDirector ? () => startEditingPrecio(s.id, s.valor_final) : undefined}
                        title={isDirector ? 'Clic para editar' : undefined}
                      >
                        {formatCLP(s.valor_final)}
                      </span>
                    )}
                  </td>
                  <td className="cons-td--center">
                    {s.estado !== 'completada' && s.estado !== 'cancelada' && (
                      <button
                        className="cons-btn-cancel"
                        title="Cancelar consulta"
                        disabled={cancelingId === s.id}
                        onClick={() => handleCancelConsultation(s.id)}
                      >
                        {cancelingId === s.id
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-ban"></i>
                        }
                      </button>
                    )}
                    {s.estado !== 'completada' && (
                      <button
                        className="cons-btn-delete"
                        title="Eliminar consulta"
                        disabled={deletingId === s.id}
                        onClick={() => handleDeleteConsultation(s.id)}
                      >
                        {deletingId === s.id
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-trash"></i>
                        }
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {hasMore && (
        <div className="cons-pagination">
          <button
            className="cons-btn cons-btn--secondary"
            onClick={() => fetchSessions(offset)}
            disabled={loading}
          >
            {loading ? 'Cargando...' : 'Cargar más'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsultationsModule;
